import {
  add,
  isNumEqual,
  mul,
  type Node,
  neg,
  num,
  numValue,
  variable,
} from "@/lib/tool-kit/calculus/ast";
import { compile } from "@/lib/tool-kit/calculus/evaluate";
import { simplify } from "@/lib/tool-kit/calculus/simplify";
import { type PointValue, valueAt } from "@/lib/tool-kit/calculus/value";

/**
 * Tangent and normal lines of y = f(x) at x = a, from the symbolic derivative.
 *
 * When f or f′ has a closed form at a (a rational number, 2√2, cos 1 + 2), the lines
 * are exact; otherwise they are decimals. If the derivative does not exist at a, the
 * one-sided difference quotients say which way it fails: a corner (finite slopes that
 * disagree), a vertical tangent (both sides run off to the same infinity), or a cusp
 * (opposite infinities).
 */

export interface Line {
  /** Slope, or null for a vertical line x = a. */
  slope: Node | null;
  slopeValue: number;
  /** y-intercept (null for a vertical line). */
  intercept: Node | null;
  interceptValue: number;
  /** y − y₀ = m(x − a), as the two sides. */
  pointSlope: { left: Node; right: Node } | null;
  /** y = mx + b as one expression of x, or null for a vertical line. */
  slopeIntercept: Node | null;
  /** Integer coefficients of A x + B y = C when the line has rational slope and intercept. */
  standard: { a: number; b: number; c: number } | null;
}

export type FailureKind = "corner" | "vertical" | "cusp" | "unbounded";

export type TangentResult =
  | { kind: "undefined" }
  | {
      kind: "not-differentiable";
      reason: FailureKind;
      left: SidedSlope;
      right: SidedSlope;
    }
  | {
      kind: "ok";
      point: PointValue;
      slope: PointValue;
      tangent: Line;
      /** Null only if the tangent is vertical (never, for a differentiable f). */
      normal: Line;
    };

const X = variable("x");
const Y = variable("y");

function lcm(a: number, b: number): number {
  const gcd = (p: number, q: number): number => (q === 0 ? p : gcd(q, p % q));
  return (a / gcd(a, b)) * b;
}

function gcdAll(values: number[]): number {
  const gcd = (p: number, q: number): number => (q === 0 ? p : gcd(q, p % q));
  return values.reduce((g, v) => gcd(g, Math.abs(v)), 0);
}

/** A x + B y = C with integer A, B, C and A ≥ 0, or null if the line is not rational. */
function standardForm(slope: Node, intercept: Node): Line["standard"] {
  if (
    slope.kind !== "num" ||
    intercept.kind !== "num" ||
    !Number.isInteger(slope.n) ||
    !Number.isInteger(intercept.n)
  ) {
    return null;
  }
  const scale = lcm(slope.d, intercept.d);
  const a = -(slope.n * scale) / slope.d;
  const b = scale;
  const c = (intercept.n * scale) / intercept.d;
  const g = gcdAll([a, b, c]) || 1;
  const sign = a < 0 || (a === 0 && b < 0) ? -1 : 1;
  return { a: (sign * a) / g || 0, b: (sign * b) / g, c: (sign * c) / g || 0 };
}

function coefficientText(
  value: number,
  letter: string,
  first: boolean
): string {
  const magnitude = Math.abs(value) === 1 ? "" : String(Math.abs(value));
  const sign = value < 0 ? "−" : "+";
  if (first) {
    return `${value < 0 ? "−" : ""}${magnitude}${letter}`;
  }
  return ` ${sign} ${magnitude}${letter}`;
}

/** "6x − y = 9", "x + 6y = 57", "x = 3", "y = −2". */
export function standardText(form: {
  a: number;
  b: number;
  c: number;
}): string {
  const terms: string[] = [];
  if (form.a !== 0) {
    terms.push(coefficientText(form.a, "x", true));
  }
  if (form.b !== 0) {
    terms.push(coefficientText(form.b, "y", terms.length === 0));
  }
  const rhs = form.c < 0 ? `−${Math.abs(form.c)}` : String(form.c);
  return `${terms.join("")} = ${rhs}`;
}

/** mx + b with the terms kept in that order, dropping a zero slope or intercept. */
function slopeInterceptForm(slope: Node, intercept: Node): Node {
  const slopeTerm = simplify(mul(slope, X));
  if (isNumEqual(slopeTerm, 0)) {
    return intercept;
  }
  if (isNumEqual(intercept, 0)) {
    return slopeTerm;
  }
  return add(
    slopeTerm,
    ...(intercept.kind === "add" ? intercept.terms : [intercept])
  );
}

export function lineThrough(
  slope: Node,
  slopeValue: number,
  x0: Node,
  y0: Node,
  y0Value: number,
  x0Value: number
): Line {
  const intercept = simplify(add(y0, neg(mul(slope, x0))));
  return {
    slope,
    slopeValue,
    intercept,
    interceptValue: y0Value - slopeValue * x0Value,
    pointSlope: {
      left: simplify(add(Y, neg(y0))),
      right: mul(slope, simplify(add(X, neg(x0)))),
    },
    slopeIntercept: slopeInterceptForm(slope, intercept),
    standard: standardForm(slope, intercept),
  };
}

function verticalLine(x0: Node, x0Value: number): Line {
  return {
    slope: null,
    slopeValue: Number.POSITIVE_INFINITY,
    intercept: null,
    interceptValue: Number.NaN,
    pointSlope: { left: X, right: x0 },
    slopeIntercept: null,
    standard: Number.isInteger(x0Value) ? { a: 1, b: 0, c: x0Value } : null,
  };
}

export interface SidedSlope {
  /** The difference quotient at the finest step. */
  value: number;
  /** True when it grows without bound as the step shrinks (or is infinite outright). */
  unbounded: boolean;
}

/** One-sided difference quotients at a, at two step sizes so a divergence shows as growth. */
export function sidedSlopes(
  f: (x: number) => number,
  a: number
): { left: SidedSlope; right: SidedSlope } {
  const scale = Math.max(1, Math.abs(a));
  const here = f(a);
  const side = (direction: -1 | 1): SidedSlope => {
    const quotient = (h: number) =>
      direction * ((f(a + direction * h) - here) / h);
    const coarse = quotient(1e-4 * scale);
    const fine = quotient(1e-8 * scale);
    const grows =
      Number.isFinite(coarse) && Number.isFinite(fine)
        ? Math.abs(fine) > 2.5 * Math.abs(coarse) && Math.abs(fine) > 50
        : !(Number.isNaN(coarse) || Number.isNaN(fine));
    return { value: fine, unbounded: grows };
  };
  return { left: side(-1), right: side(1) };
}

function classify(left: SidedSlope, right: SidedSlope): FailureKind {
  if (left.unbounded && right.unbounded) {
    return Math.sign(left.value) === Math.sign(right.value)
      ? "vertical"
      : "cusp";
  }
  if (left.unbounded || right.unbounded) {
    return "vertical";
  }
  const [l, r] = [left.value, right.value];
  if (Number.isNaN(l) || Number.isNaN(r)) {
    return "unbounded";
  }
  return Math.abs(l - r) > 1e-3 * Math.max(1, Math.abs(l))
    ? "corner"
    : "unbounded";
}

/** Tangent and normal lines of f at x = a, given f′ and the point as exact nodes. */
export function tangentAt(
  f: Node,
  derivative: Node,
  a: Node,
  aValue: number
): TangentResult {
  const point = valueAt(f, "x", a, aValue);
  if (!Number.isFinite(point.value) || point.exact === null) {
    return { kind: "undefined" };
  }
  const slope = valueAt(derivative, "x", a, aValue);
  if (!Number.isFinite(slope.value) || slope.exact === null) {
    const { left, right } = sidedSlopes(compile(f, "x"), aValue);
    return {
      kind: "not-differentiable",
      reason: classify(left, right),
      left,
      right,
    };
  }
  const tangent = lineThrough(
    slope.exact,
    slope.value,
    a,
    point.exact,
    point.value,
    aValue
  );
  const flat =
    numValue(slope.exact.kind === "num" ? slope.exact : num(slope.value)) === 0;
  const normal = flat
    ? verticalLine(a, aValue)
    : lineThrough(
        simplify(
          mul(num(-1), { kind: "pow", base: slope.exact, exp: num(-1) })
        ),
        -1 / slope.value,
        a,
        point.exact,
        point.value,
        aValue
      );
  return { kind: "ok", point, slope, tangent, normal };
}
